// -----------------------------------------------------------------------------
// Wave 2 · Batch 2Q — Pillar Comparison Widget (Feb-2026)
// -----------------------------------------------------------------------------
// Head-to-head "X vs Y vs Z" comparison matrix rendered on every /courses/*
// pillar page. Captures the single-highest-intent query pattern that isn't
// yet targeted by the platform:
//
//     "CBSE vs ICSE vs IB"
//     "IB vs A-Level vs AP for US admissions"
//     "IGCSE vs CBSE for Class 10"
//     "NEET vs JEE for a PCB / PCM student"
//
// Design principles:
//   1. Every comparison group is bounded by real search-intent — we do NOT
//      compare curricula that no parent ever compares (no "IB vs NEET").
//   2. Every row is factual, side-agnostic and unbiased — the widget must
//      read like an examiner-neutral primer, NOT a sales pitch. The final
//      "Anand Tutorials price band" row is the only self-referential row.
//   3. Every pillar page shows the CURRENT pillar highlighted in the header
//      row (amber-accent) and the competitors in neutral slate — so the
//      user's eye lands on their pillar first.
//   4. Skips: ielts-toefl, spoken-english, coding — no natural head-to-head
//      comparator that satisfies real search intent.

// -----------------------------------------------------------------------------
// PILLAR META — labels + one-line taglines used inside the matrix headers
// -----------------------------------------------------------------------------
export const COMPARISON_PILLAR_META = {
    "cbse":         { short: "CBSE",         full: "CBSE (NCERT)" },
    "icse-isc":     { short: "ICSE / ISC",   full: "ICSE (CISCE) / ISC" },
    "state-boards": { short: "State Boards", full: "State Board (MPBSE / MH / UP …)" },
    "ib":           { short: "IB DP",        full: "IB (PYP · MYP · DP)" },
    "igcse":        { short: "IGCSE",        full: "Cambridge IGCSE" },
    "a-level":      { short: "A-Level",      full: "Cambridge / Edexcel A-Level" },
    "ap":           { short: "AP",           full: "AP (College Board)" },
    "neet":         { short: "NEET",         full: "NEET (Medical)" },
    "jee":          { short: "JEE",          full: "JEE Main + Advanced" },
};

// -----------------------------------------------------------------------------
// COMPARISON ROWS — one row per dimension, per group.
// -----------------------------------------------------------------------------
// Row shape: { label, cells: { <pillarSlug>: "cell text" } }.
// -----------------------------------------------------------------------------

// School-board comparison (Indian + International) — used by cbse, icse-isc,
// state-boards, ib, igcse. Every cell is fact-checked as of Feb-2026.
const _SCHOOL_ROWS = [
    {
        label: "Awarding body",
        cells: {
            "cbse":         "CBSE, Delhi — Central Government of India",
            "icse-isc":     "CISCE, Delhi — private national council",
            "state-boards": "State board (MPBSE, MSBSHSE, UP-Board, RBSE …)",
            "ib":           "IB Organization, Geneva — worldwide",
            "igcse":        "Cambridge Assessment International Education, UK",
            "a-level":      "Cambridge International / Pearson Edexcel, UK",
            "ap":           "College Board, USA",
        },
    },
    {
        label: "Grades covered",
        cells: {
            "cbse":         "Class 1 → 12 (single continuous NCERT track)",
            "icse-isc":     "Class 1 → 10 (ICSE) + 11 → 12 (ISC)",
            "state-boards": "Class 1 → 12 in the state's regional / Hindi / English medium",
            "ib":           "PYP (age 3-12) · MYP (11-16) · DP (16-19) · CP",
            "igcse":        "Typically Year 9-11 / Class 9-10 (age 14-16)",
            "a-level":      "AS (Year 12) + A2 (Year 13) — age 16-18",
            "ap":           "One-year subject exams, most in Grade 11-12 (age 16-18)",
        },
    },
    {
        label: "Assessment style",
        cells: {
            "cbse":         "One board exam per subject at Class 10 & 12 + 30% competency questions",
            "icse-isc":     "Council board exam at Class 10 (ICSE) & 12 (ISC) — long structured written answers, English-language-heavy",
            "state-boards": "Single terminal board exam at Class 10 & 12 — SCERT-textbook-driven",
            "ib":           "Coursework (IAs, EE, TOK) 20-30% + terminal written papers 70-80% at DP",
            "igcse":        "Modular subject-by-subject terminal papers with tiered (Core / Extended) grade caps",
            "a-level":      "Modular linear papers per subject — grades A* → E",
            "ap":           "Single 3-hour subject exam per year, scored 1-5 (a 5 is 'extremely well qualified')",
        },
    },
    {
        label: "Best fit for",
        cells: {
            "cbse":         "Indian students planning to sit JEE / NEET or apply to Indian universities",
            "icse-isc":     "English-heavy readers, humanities strengths, Indian and Commonwealth university applicants",
            "state-boards": "Regional-medium learners staying within the state ecosystem — MP / MH / UP university admissions",
            "ib":           "Global-mobile families, holistic-profile applicants, US / UK / Ivy-League + top Indian college aspirants",
            "igcse":        "Class 9-10 students choosing an international pathway leading into A-Level, IB DP or the American Diploma",
            "a-level":      "Students targeting UK / Singapore / HK / Australia universities and a subject-focused 3-4-subject profile",
            "ap":           "US-college-bound students (STEM + humanities) seeking course credit + a strong college-app signal",
        },
    },
    {
        label: "University recognition",
        cells: {
            "cbse":         "Accepted at every Indian university + widely accepted internationally with Class 12 marksheet",
            "icse-isc":     "Accepted at every Indian university + strong international acceptance (especially UK / Commonwealth)",
            "state-boards": "Accepted at every Indian university; state-quota advantage for local government colleges",
            "ib":           "Accepted worldwide — 90+ countries · direct entry to top US / UK / EU / AUS universities",
            "igcse":        "Accepted worldwide as a Class 10 equivalent + gateway to A-Level / IB DP / AP",
            "a-level":      "Direct entry to UK Russell Group, Singapore NUS / NTU, HKU, Aussie Go8, plus Indian universities",
            "ap":           "Course credit at 3,900+ US colleges (typically for scores of 3-5) + strong signal for admissions",
        },
    },
    {
        label: "Study intensity",
        cells: {
            "cbse":         "Content-heavy — objective + application questions across 5-6 subjects",
            "icse-isc":     "Depth + English-writing intensive — 6-7 subjects at Class 10, 4-5 at Class 12",
            "state-boards": "Textbook-defined scope — memorisation-friendly for the state paper pattern",
            "ib":           "Very high — 6 subjects + TOK + EE + CAS · 240+ teaching hours per HL subject",
            "igcse":        "Moderate — 5-9 subjects sat modularly · Extended tier is stretch",
            "a-level":      "Deep and narrow — 3-4 subjects at university-first-year rigour",
            "ap":           "Flexible — students self-select 3-8 AP subjects across two years",
        },
    },
    {
        label: "Anand Tutorials price band (1:1 online)",
        cells: {
            "cbse":         "INR 400-2,500 per hour",
            "icse-isc":     "INR 500-2,500 per hour",
            "state-boards": "INR 300-1,500 per hour",
            "ib":           "INR 1,200-3,500 per hour (or USD 25-70 for NRI families)",
            "igcse":        "INR 800-3,000 per hour",
            "a-level":      "INR 1,000-3,500 per hour",
            "ap":           "USD 25-70 per hour (INR 1,000-3,500 for India)",
        },
    },
];

// Indian-entrance-exam comparison — used by neet, jee only.
const _ENTRANCE_ROWS = [
    {
        label: "Awarding body",
        cells: {
            "neet": "NTA (National Testing Agency) — Union Ministry of Education",
            "jee":  "NTA (Main) + IIT-Roorkee/-Kanpur/-Bombay etc. (Advanced) rotational",
        },
    },
    {
        label: "Test pattern",
        cells: {
            "neet": "Single 3-hr 20-min offline paper — 200 MCQs across Physics + Chemistry + Biology (Botany + Zoology)",
            "jee":  "Main: 3-hr online CBT (PCM MCQs + integer) · Advanced: 2 × 3-hr online papers with multi-select + numerical",
        },
    },
    {
        label: "Marking + negative marking",
        cells: {
            "neet": "+4 correct / -1 incorrect / 0 unattempted · total 720 marks",
            "jee":  "Main: +4 / -1 · Advanced: variable (+3 / 0 / -1 partials on multi-selects) · total ~300 per paper",
        },
    },
    {
        label: "Class 12 subject stack",
        cells: {
            "neet": "PCB (Physics + Chemistry + Biology) — mandatory Biology",
            "jee":  "PCM (Physics + Chemistry + Mathematics) — mandatory Mathematics",
        },
    },
    {
        label: "Colleges opened up",
        cells: {
            "neet": "MBBS / BDS / AYUSH seats at AIIMS, JIPMER, MAMC, KGMU, government + private medical colleges — 1.1 lakh MBBS seats",
            "jee":  "Main: NITs, IIITs, GFTIs + state-quota engineering · Advanced: 23 IITs + IISc + IIST Thiruvananthapuram",
        },
    },
    {
        label: "Ideal starting class",
        cells: {
            "neet": "Class 11 foundation + Class 12 intensive · repeat / drop-year common for the top-1% MBBS colleges",
            "jee":  "Class 9-10 early foundation + Class 11-12 intensive · JEE Advanced attempts capped at 2",
        },
    },
    {
        label: "Anand Tutorials price band (1:1 online)",
        cells: {
            "neet": "INR 800-3,000 per hour",
            "jee":  "INR 1,000-3,500 per hour",
        },
    },
];

// -----------------------------------------------------------------------------
// COMPARISON GROUPS — per current-pillar-slug → { columns: [slug…], rows }
// -----------------------------------------------------------------------------
// The FIRST column is always the CURRENT pillar (rendered highlighted).
// -----------------------------------------------------------------------------

export const PILLAR_COMPARISON_GROUPS = {
    "cbse": {
        columns: ["cbse", "icse-isc", "ib", "igcse"],
        rows: _SCHOOL_ROWS,
        headline: "How does CBSE compare with ICSE, IB and IGCSE?",
        sub: "A neutral side-by-side matrix for parents weighing CBSE against the other three most-shortlisted boards in India today.",
    },
    "icse-isc": {
        columns: ["icse-isc", "cbse", "ib", "igcse"],
        rows: _SCHOOL_ROWS,
        headline: "How does ICSE / ISC compare with CBSE, IB and IGCSE?",
        sub: "A neutral side-by-side matrix for parents weighing the CISCE Council system against India's other three most-shortlisted boards.",
    },
    "state-boards": {
        columns: ["state-boards", "cbse", "icse-isc"],
        rows: _SCHOOL_ROWS,
        headline: "How do Indian State Boards compare with CBSE and ICSE / ISC?",
        sub: "A neutral side-by-side matrix for families evaluating the state-board pathway against the two central Indian boards.",
    },
    "ib": {
        columns: ["ib", "igcse", "a-level", "ap"],
        rows: _SCHOOL_ROWS,
        headline: "How does IB compare with IGCSE, A-Level and AP?",
        sub: "A neutral side-by-side matrix for families weighing the IB Diploma against the other three globally-recognised pathways.",
    },
    "igcse": {
        columns: ["igcse", "ib", "a-level", "cbse"],
        rows: _SCHOOL_ROWS,
        headline: "How does Cambridge IGCSE compare with IB, A-Level and CBSE?",
        sub: "A neutral side-by-side matrix for Class 9-10 families choosing between the Cambridge IGCSE and the IB / A-Level / CBSE pathways.",
    },
    "a-level": {
        columns: ["a-level", "ib", "ap", "igcse"],
        rows: _SCHOOL_ROWS,
        headline: "How do A-Levels compare with IB, AP and IGCSE?",
        sub: "A neutral side-by-side matrix for Year-12 / Class-11 families choosing between A-Levels, the IB Diploma, AP and IGCSE-continuation pathways.",
    },
    "ap": {
        columns: ["ap", "ib", "a-level", "igcse"],
        rows: _SCHOOL_ROWS,
        headline: "How does AP compare with IB, A-Level and IGCSE?",
        sub: "A neutral side-by-side matrix for US-college-bound families weighing College Board APs against the IB Diploma, A-Levels and Cambridge IGCSE.",
    },
    "neet": {
        columns: ["neet", "jee"],
        rows: _ENTRANCE_ROWS,
        headline: "NEET vs JEE — which is right for your Class 11-12 aspirant?",
        sub: "A neutral side-by-side matrix for PCB / PCM families weighing India's two flagship entrance exams.",
    },
    "jee": {
        columns: ["jee", "neet"],
        rows: _ENTRANCE_ROWS,
        headline: "JEE vs NEET — which is right for your Class 11-12 aspirant?",
        sub: "A neutral side-by-side matrix for PCM / PCB families weighing India's two flagship entrance exams.",
    },
};

/**
 * Slug → comparison group. Returns null for pillars that have no natural
 * comparator (ielts-toefl, spoken-english, coding) — the widget will not
 * render on those pages.
 */
export function getPillarComparison(slug) {
    return PILLAR_COMPARISON_GROUPS[slug] || null;
}

/**
 * Map a pillar slug → the internal /courses/<slug> URL for the linked
 * competitor cells. Skips the current column (no self-link).
 */
export function pillarHref(slug) {
    return `/courses/${slug}`;
}
